import {identity} from 'lodash';
import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {CellsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {LegacyInteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {FetchCellsCachePoliciesEnum} from '../../../store/panels/cells/cellsPanelActions';
import {
  InboxesConversationsParameters,
  InboxesTeammateMessagesParameters,
} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {ErrorReporterContainer} from '../../errors/errorReporterContainer';
import {ReadStatusInfoBubbleHighlight} from '../../infoBubbles/readStatusInfoBubbleHighlight';
import {CellsRegion} from './cellsRegion';
import {CellsContentChildrenRenderer, CellsContentParametersPathBuilder} from './content/cellsContent';
import {CellsContentContainer} from './content/cellsContentContainer';

/*
 * Props.
 */

type CellsParametersPathBuilder = CellsContentParametersPathBuilder;
export type CellsChildrenRenderer = CellsContentChildrenRenderer;

interface CellsProps {
  panelId: CellsPanelId;
  buildParametersPath?: CellsParametersPathBuilder;
  onParametersChange: (
    parameters: InboxesConversationsParameters | InboxesTeammateMessagesParameters,
  ) => void;
  cellsHeader?: ReactNode;
  children?: CellsChildrenRenderer;
}

/*
 * Style.
 */

const StyledCellsRegion = styled(CellsRegion)`
  display: grid;
  grid-template-rows: auto auto 1fr; /* optional header, refinements, cells */
  background-color: ${(p) => p.theme.appBackground};
  // Establish a new stacking context to contain the P2 sort options.
  z-index: 0;
`;

/*
 * Component.
 */

export const Cells: FC<CellsProps> = (props) => {
  const {cellsHeader, buildParametersPath, onParametersChange} = props;
  const children: CellsChildrenRenderer = props.children ?? identity;

  const regionRef = useRef<HTMLDivElement>(null);

  return (
    <ErrorReporterContainer isTopLevel={false}>
      <CellsContentContainer
        panelId={props.panelId}
        cachePolicy={FetchCellsCachePoliciesEnum.CACHE_THEN_NETWORK}
        buildParametersPath={buildParametersPath}
        onParametersChange={onParametersChange}
      >
        {(cells, options) =>
          children(
            <LegacyInteractionParent interactionId="cells">
              <StyledCellsRegion ref={regionRef}>
                {cells}
                {cellsHeader}
              </StyledCellsRegion>

              <ReadStatusInfoBubbleHighlight anchor={regionRef} />
            </LegacyInteractionParent>,
            options,
          )
        }
      </CellsContentContainer>
    </ErrorReporterContainer>
  );
};
